import type { HTMLAttributes, ReactNode } from 'react';
import { cn } from '@/lib/utils';

/**
 * P5-4 Commit A — the book flow's content lane.
 *
 * Block 1 (Sebastian ruling 2026-09-09) — the content now sits inside
 * a white card on the warm-paper page background. The lane widened
 * from 680 → 860 px per spec 1.2, and the outer wrapper carries the
 * phone gutter so the card can reach its full 860 px on tablet+
 * without side-clipping.
 *
 * ═══ TWO LAYERS ═════════════════════════════════════════════════════
 *
 * 1. Outer wrapper — provides the top/bottom rhythm plus the phone
 *    gutter. Not the card. Vertical margins my call: `my-6` phone,
 *    `my-10` tablet, `my-14` desktop. Correct by eye later.
 * 2. Inner card — 860 px max, mx-auto, `bg-book-card` (#FFFFFF),
 *    `rounded-[28px]`, soft shadow (`shadow-book-card` token),
 *    generous padding. Phone padding leans smaller so the card
 *    doesn't dwarf phone content; tablet+ steps up. Radius stays
 *    28 px at every width per spec 1.2. My call on the exact
 *    padding steps.
 *
 * One width for all six steps. The card must NOT jump between
 * screens.
 *
 * The book Kopfleiste does NOT wrap in this lane — it uses the
 * shared `<ContentContainer>` default variant to stay flush with
 * the audio Kopfleiste (spec §2 "Die Kopfleiste läuft nicht in
 * dieser Bahn"). BookLane wraps ONLY the page content.
 */
export interface BookLaneProps extends HTMLAttributes<HTMLDivElement> {
  readonly children?: ReactNode;
}

export function BookLane({ className, children, ...props }: BookLaneProps) {
  return (
    <div className={cn('px-4 py-6 md:px-6 md:py-10 desktop:py-14', className)} {...props}>
      <div className="mx-auto max-w-[860px] rounded-[28px] bg-book-card px-6 py-10 shadow-[var(--shadow-book-card)] md:px-12 md:py-14 desktop:px-16 desktop:py-16">
        {children}
      </div>
    </div>
  );
}
